export interface DisposableMarker extends H.map.Marker {
  dispose: () => void
}

export interface MarkerEvent {
  target: H.map.Marker
}

export function createMarker(position: H.geo.IPoint, iconMarkup: string): H.map.Marker {
  return new H.map.Marker(position, {
    data: {},
    icon: new H.map.Icon(iconMarkup),
  })
}

export enum Highlight {
  none = 'none',
  hover = 'hover',
  pdc = 'pdc',
}

export interface HighlightableMarkerData {
  id: string
  highlight: Highlight
  zIndex: number
  iconElement?: SVGElement
  onAttachOnce?: () => void
}

export interface HighlightableMarkerItem {
  id: string
  position: H.geo.IPoint
}

export type Markers = Map<string, H.map.Marker>

export function createHighlightableMarker(
  position: H.geo.IPoint,
  iconMarkup: string,
  id: string,
  zIndex: number
): H.map.DomMarker {
  // "z-index" is not only supplied to marker options,
  // but also attached to its data, so it could be restored on highlighting removal
  const data: HighlightableMarkerData = { highlight: Highlight.none, id, zIndex }

  // DOM marker is used because:
  // * regular markers can't rasterize a referenced "<use />" children element
  // * markers are rescaling when getting highlighted, which requires a vector icon to avoid distortion
  return new H.map.DomMarker(position, {
    data,
    icon: new H.map.DomIcon(iconMarkup, {
      onAttach: (iconElement, icon, marker) => {
        // "onAttachOnce" is read from "marker.getData()" because it doesn't exist on the local "data" variable,
        // but may exist on "marker.getData()", when it's accessed elsewhere during the marker's lifecycle
        const { onAttachOnce } = marker.getData() as HighlightableMarkerData

        // it's important to assign "iconElement" before calling "onAttachOnce()"
        // because that callback may rely on the "iconElement"
        marker.setData({ ...data, iconElement })

        if (onAttachOnce !== undefined) {
          onAttachOnce()
        }
      },
      onDetach: (iconElement, icon, marker) => marker.setData(data),
    }),
    zIndex,
  })
}

export function removeMarkers(map: H.Map, markers: Markers): Markers {
  markers.forEach((marker: DisposableMarker) => marker.dispose())
  return new Map()
}
